import { forwardRef, Ref, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseSelectProps } from '~/components/inputs/Select/Select';
import SimpleSelect from '~/components/inputs/Select/SimpleSelect';
import { PaymentMethod } from '~/types/flow';

interface Props extends BaseSelectProps {
  excludedPaymentMethods?: PaymentMethod[];
  onChange: (value: PaymentMethod | null) => void;
  value: PaymentMethod | null;
  allowEmpty?: boolean;
}

const allMethods = Object.values(PaymentMethod);

const FlowPaymentMethodSelect = forwardRef(
  ({ excludedPaymentMethods, onChange, allowEmpty, ...props }: Props, ref?: Ref<HTMLDivElement>) => {
    const { t } = useTranslation();
    const methods = useMemo(
      () => (excludedPaymentMethods ? allMethods.filter((o) => !excludedPaymentMethods.includes(o)) : allMethods),
      [excludedPaymentMethods],
    );

    const options = useMemo(() => {
      const emptyOption = { value: null, label: t('NONE') };
      const result = methods.map((option) => ({ value: option, label: t(option) }));
      return allowEmpty ? [emptyOption, ...result] : result;
    }, [methods, t, allowEmpty]);

    const handleChange = useCallback((value: string | null) => onChange(value as PaymentMethod | null), [onChange]);
    return <SimpleSelect {...props} emptyValue="" onChange={handleChange} options={options} ref={ref} />;
  },
);

export default FlowPaymentMethodSelect;
